import { CookiePreferences } from '../../../redux/cookieConsent/cookieConsentSlice';
import { initGA } from '../../../utils/analytics';

export const setCookie = (
  name: string,
  value: string,
  days = 365,
  path = '/',
): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=${path};SameSite=Lax`;
};

export const getCookie = (name: string): string | null => {
  const nameWithEquals = `${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');

  for (const cookie of cookieArray) {
    let currentCookie = cookie;
    while (currentCookie.charAt(0) === ' ') {
      currentCookie = currentCookie.substring(1);
    }
    if (currentCookie.indexOf(nameWithEquals) === 0) {
      return currentCookie.substring(
        nameWithEquals.length,
        currentCookie.length,
      );
    }
  }
  return null;
};

export const deleteCookie = (name: string, path = '/'): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path};`;
};

export const clearCookiesByCategory = (
  category: keyof CookiePreferences,
  preferences: CookiePreferences,
): void => {
  if (!preferences[category]) {
    // Define which cookies belong to each category
    const cookiesByCategory = {
      necessary: [], // Never clear necessary cookies
      analytics: [
        '_ga',
        '_gid',
        '_gat',
        '__utma',
        '__utmb',
        '__utmc',
        '__utmt',
        '__utmz',
      ],
      marketing: ['_fbp', '_fbc', 'fr', 'tr', '__pinterest_ct_ua'],
      preferences: ['theme', 'language', 'user_preferences'],
    };

    const cookiesToClear = cookiesByCategory[category] || [];

    cookiesToClear.forEach((cookieName) => {
      deleteCookie(cookieName);
      deleteCookie(cookieName, '/');
    });
  }
};

export const enforceConsentPreferences = (
  preferences: CookiePreferences,
): void => {
  // Clear cookies for categories that are not consented to
  Object.keys(preferences).forEach((category) => {
    if (category !== 'necessary') {
      clearCookiesByCategory(category as keyof CookiePreferences, preferences);
    }
  });
};

export const getConsentedCategories = (
  preferences: CookiePreferences,
): string[] => {
  return Object.entries(preferences)
    .filter(([_, isConsented]) => isConsented)
    .map(([category]) => category);
};

export const initializeAnalytics = (preferences: CookiePreferences): void => {
  if (preferences.analytics && typeof window !== 'undefined') {
    console.log(
      'Analytics enabled with user consent - initializing Google Analytics',
    );

    if (!window.gtag) {
      initGA();
    }
  } else {
    console.log('Analytics disabled - user has not consented');
  }
};

export const initializeMarketing = (preferences: CookiePreferences): void => {
  if (preferences.marketing && typeof window !== 'undefined') {
    console.log('Marketing tools enabled with user consent');

    // Initialize marketing tools here when user consents
    // Example: Facebook Pixel, Google Ads, etc.
  } else {
    console.log('Marketing tools disabled - user has not consented');
  }
};

export const initializeAllConsentPreferences = (
  preferences: CookiePreferences,
): void => {
  // Centralized place to manage all consent initialization functions
  // To add new categories: just add the new initialization function here
  // To remove categories: just remove the function call from here

  initializeAnalytics(preferences);
  initializeMarketing(preferences);
  enforceConsentPreferences(preferences);

  console.log('All consent preferences initialized:', preferences);
};
